import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useStoreSettings } from '../hooks/useStoreSettings';
import SeoHead from '../components/seo/SeoHead';

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  featured_image: string | null;
  published_at: string | null;
  created_at: string | null;
}

const formatDate = (d: string | null): string => {
  if (!d) return '';
  try {
    return new Date(d).toLocaleDateString('sr-RS', { day: 'numeric', month: 'long', year: 'numeric' });
  } catch {
    return '';
  }
};

const BlogList: React.FC = () => {
  const { settings, loading: settingsLoading } = useStoreSettings();
  const blogEnabled = (settings?.header_content as any)?.showBlog === true;
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const { data, error } = await supabase
          .from('blog_posts')
          .select('id, title, slug, excerpt, featured_image, published_at, created_at')
          .eq('status', 'published');
        if (error) throw error;
        const sorted = (data || []).sort((a: BlogPost, b: BlogPost) => {
          const da = new Date(a.published_at || a.created_at || 0).getTime();
          const db = new Date(b.published_at || b.created_at || 0).getTime();
          return db - da;
        });
        setPosts(sorted);
      } catch (err) {
        console.error('Error loading blog posts:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, []);

  const wrap: React.CSSProperties = { maxWidth: 1100, margin: '0 auto', padding: '32px 20px' };

  if (!settingsLoading && !blogEnabled) {
    return (
      <div style={wrap}>
        <SeoHead title="Blog" noindex />
        <h1>Blog</h1>
        <p>Blog trenutno nije dostupan.</p>
      </div>
    );
  }

  return (
    <div style={wrap}>
      <SeoHead
        title="Blog"
        description="Saveti i vodiči za izbor i negu saksija, žardinjera i biljaka."
        type="website"
      />
      <h1 style={{ marginBottom: 8 }}>Blog</h1>
      <p style={{ color: '#6b7280', marginTop: 0, marginBottom: 28 }}>
        Saveti i vodiči za izbor i negu saksija, žardinjera i biljaka.
      </p>

      {loading ? (
        <p>Učitavanje...</p>
      ) : posts.length === 0 ? (
        <p>Još nema objavljenih članaka. Uskoro stižu novi tekstovi.</p>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: 24,
          }}
        >
          {posts.map(post => (
            <Link
              key={post.id}
              to={`/blog/${post.slug}`}
              style={{
                display: 'flex',
                flexDirection: 'column',
                border: '1px solid #eee',
                borderRadius: 10,
                overflow: 'hidden',
                textDecoration: 'none',
                color: 'inherit',
                background: '#fff',
                boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
              }}
            >
              {post.featured_image && (
                <img
                  src={post.featured_image}
                  alt={post.title}
                  style={{ width: '100%', height: 180, objectFit: 'cover' }}
                  loading="lazy"
                />
              )}
              <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
                {(post.published_at || post.created_at) && (
                  <span style={{ fontSize: 13, color: '#9ca3af' }}>
                    {formatDate(post.published_at || post.created_at)}
                  </span>
                )}
                <h2 style={{ fontSize: 18, margin: 0, lineHeight: 1.3 }}>{post.title}</h2>
                {post.excerpt && (
                  <p style={{ fontSize: 14, color: '#4b5563', margin: 0 }}>{post.excerpt}</p>
                )}
                <span style={{ fontSize: 14, color: '#2563eb', marginTop: 4 }}>Pročitaj više</span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default BlogList;
